import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { _HttpClient } from '@delon/theme';
import { Subject, takeUntil } from 'rxjs';

import { SelectBaseComponent } from '../select-base';

@Component({
  selector: 'app-simple-multi-select',
  template: `
    <nz-select
      #selector
      nzMode="multiple"
      nzPlaceHolder="All items"
      [(ngModel)]="values"
      [nzDropdownRender]="renderTemplate"
      [nzDropdownMatchSelectWidth]="false"
      [nzBorderless]="borderless"
      [nzAllowClear]="allowClear"
      [nzDisabled]="allowEdit === false"
      class="width-100"
      (nzOpenChange)="onChangeOpen($event)"
      (nzScrollToBottom)="loadMore()"
      [disabled]="disabled"
    >
      <nz-option nzLabel="Select All" nzValue="select all" nzCustomContent
        >Select All</nz-option
      >
      <ng-container *ngFor="let o of list">
        <nz-option
          [nzValue]="getValue(o)"
          [nzLabel]="o.name"
          [nzDisabled]="isPreSelectItem(o)"
          nzCustomContent
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ o.code }}</strong>
              :
              <span> {{ o.name }}</span>
            </div>
            <i
              *ngIf="isSelected(o)"
              nz-icon
              nzType="check"
              class="text-primary"
            ></i>
          </div>
        </nz-option>
      </ng-container>
    </nz-select>
    <ng-template #renderTemplate>
      <nz-spin *ngIf="loading"></nz-spin>
      <nz-divider class="m-none"></nz-divider>
    </ng-template>
  `,
  styles: [
    `
      nz-select {
        width: 100%;
      }

      .loading-icon {
        margin-right: 8px;
      }

      ::ng-deep .ant-select-item-option-state {
        display: none !important;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SimpleMultiSelectComponent),
      multi: true
    }
  ]
})
export class SimpleMultiSelectComponent
  extends SelectBaseComponent
  implements OnChanges, OnDestroy
{
  @Input('url') _url: string = '';
  @Input() preSelectItemId: string = '';
  @Input() name? = '';
  private destroy$ = new Subject<void>();

  constructor(http: _HttpClient, cdr: ChangeDetectorRef) {
    super(http, cdr);
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['_url']) {
      this.url = this._url;
    }
  }

  private _values: any[] = [];
  public get values(): any[] {
    return this._values;
  }
  public set values(v: any[]) {
    if (v.includes('select all')) {
      this.selectAllOptions();
    } else {
      const uniqueValues = this.removeDuplicates(v);
      this._values = this.checkExistPreSelectItemInValues(uniqueValues);
      this.writeValue(this._values);
    }
  }

  private removeDuplicates(values: any[]): any[] {
    const seen = new Set();
    return values.filter(item => {
      const id = item?.id || item;
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }

  isSelected(option: any): boolean {
    const optionValue = this.getValue(option);
    const optionId = optionValue?.id || optionValue;
    return this._values.some(value => {
      const valueId = value?.id || value;
      return valueId === optionId;
    });
  }

  isPreSelectItem(option: any): boolean {
    // Disable the option if it matches the preSelectItemId
    if (!this.preSelectItemId) {
      return false;
    }
    const optionId = option?.id || option;
    return String(optionId) === String(this.preSelectItemId);
  }

  override getUrl(searchValue: string): string {
    return this._url;
  }
  optionList: string[] = [];
  selectedUser?: string;
  selectAllOptions() {
    this._values = [...this.list.map(o => this.getValue(o))];
    this.writeValue(this.values);
  }
  async selectPreSelectItem() {
    if (!this.list.length) {
      await this.fetchData('');
    }
    setTimeout(() => {
      if (this.preSelectItemId && !this.disabled) {
        const preSelectItem = this.list.find(
          o => o.id === this.preSelectItemId
        );
        if (preSelectItem) {
          this.values = [this.getValue(preSelectItem), ...this.values];
        }
      }
    });
  }
  checkExistPreSelectItemInValues(v: any[]) {
    // If no preSelectItemId is set, return values as-is
    if (!this.preSelectItemId) {
      return v;
    }

    // Check if preSelectItem is already in the new values
    const preSelectItemInValues = v.some(item => {
      const itemId = typeof item === 'object' ? item?.id : item;
      return String(itemId) === String(this.preSelectItemId);
    });

    // If preSelectItem is already in values, return as-is
    if (preSelectItemInValues) {
      return v;
    }

    // PreSelectItem is not in values, always add it back
    // This ensures the preSelectItemId option cannot be removed
    const preSelectItem = this.list.find(
      o => String(o.id) === String(this.preSelectItemId)
    );
    if (preSelectItem) {
      return [this.getValue(preSelectItem), ...v];
    }

    // If preSelectItem not found in list, return values as-is
    return v;
  }
  override async writeValue(value: any) {
    if (value) {
      this._values = this.removeDuplicates(value);
      if (value?.length > 0 && !this.list?.length && !this.loading) {
        this.fetchData('').then(() => this.covertListToObj());
      } else {
        this.covertListToObj();
      }
      this.onChange(this.values);
      this.cdr.markForCheck();
    } else {
      this._values = [];
    }
  }
  private covertListToObj() {
    if (this._values.length > 0) {
      this._values = [...this.values].map(o => {
        if (typeof o === 'string') {
          return this.list.find(l => l.id == o);
        } else {
          return o;
        }
      });
    }
  }

  async onChangeOpen(open: boolean) {
    if (open) {
      await this.fetchData('');
    }
  }
}
